// C:\Users\User\Desktop\Bresco\bresco-franquicies-frontend\src\components\ui\Modals\UploadFileModal.js
import useFetch from 'hooks/useFetch'
import { useToast } from 'hooks/useToast'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import {
  LuArrowDownToLine,
  LuCheck,
  LuCloudAlert,
  LuCloudUpload,
  LuLoaderCircle,
  LuMoveLeft,
  LuMoveRight,
  LuRefreshCcw,
  LuSearch,
  LuTriangleAlert,
  LuUpload,
  LuX
} from 'react-icons/lu'

export default function UploadFileModal({ onClose, uploadExcelURL, handleRefresh, handleAnalyzeExcel }) {
  const { showToast } = useToast()
  const [saved, save] = useFetch()

  const [step, setStep] = useState(1)
  const [file, setFile] = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const [loading, setLoading] = useState(0)

  const [analyzedData, setAnalyzedData] = useState(false)
  const [rowLogs, setRowLogs] = useState([])
  const [processedRowsCount, setProcessedRowsCount] = useState(0)
  const [insertedRowsCount, setInsertedRowsCount] = useState(0)
  const [duplicatedRowsCount, setDuplicatedRowsCount] = useState(0)
  const [importError, setImportError] = useState(null)

  // NUEVO: cuántos siniestros se han sincronizado (backend: synced_siniestros)
  const [syncedSiniestrosCount, setSyncedSiniestrosCount] = useState(0)

  const uploadDocument = (e) => setFile(e?.target?.files?.[0] || null)

  const dropHandler = (ev) => {
    ev.preventDefault()

    const files =
      ev.dataTransfer?.files && ev.dataTransfer.files.length
        ? Array.from(ev.dataTransfer.files)
        : []

    if (files.length > 1) {
      showToast('Por favor, suelta solo un archivo.', 'error')
      return
    }
    setFile(files[0] || null)
  }

  const dragOverHandler = (ev) => ev.preventDefault()

  const handleUploadExcel = async (url, items, apiCall) => {
    await apiCall({
      url,
      method: 'POST',
      body: { rows: items } // el backend espera req.body.rows
    })
  }

  // Reset “soft” cuando cambiamos de fichero
  useEffect(() => {
    setImportError(null)
    setSyncedSiniestrosCount(0)
    setProcessedRowsCount(0)
    setInsertedRowsCount(0)
    setDuplicatedRowsCount(0)
  }, [file])

  useEffect(() => {
    if (!saved?.isCalled) return

    if (saved?.message || saved?.data) {
      setTimeout(() => {
        // inserted viene del backend; fallback a analyzedData.length
        const inserted = Number(saved?.data?.inserted ?? analyzedData?.length ?? 0) || 0
        const synced = Number(saved?.data?.synced_siniestros ?? 0) || 0

        setProcessedRowsCount(inserted)
        setInsertedRowsCount(inserted)
        setDuplicatedRowsCount(0) // en TRUNCATE+INSERT no aplica "duplicados"
        setSyncedSiniestrosCount(synced)

        setStep(4)
      }, 1500)
    }

    if (saved?.error) {
      setTimeout(() => {
        setImportError(saved?.error?.toString() || 'No se han podido importar los registros...')
        setStep(4)
      }, 1500)
    }
  }, [saved?.loading]) // eslint-disable-line

  return (
    <div className="fixed inset-0 z-10 overflow-y-auto bg-base/75" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center h-screen min-h-screen px-4 pt-4 pb-4 text-center sm:items-end sm:block sm:p-0">
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <div className="relative inline-flex flex-col w-full h-full px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-sm shadow-xl sm:h-5/6 md:h-4/6 rtl:text-right dark:bg-gray-900 sm:my-8 sm:align-middle sm:w-11/12 md:w-10/12 xl:w-8/12 lg:p-8">
          {/* Steps */}
          <ol className="flex items-center justify-between w-full md:space-x-2 lg:space-x-8">
            {/* 1 */}
            <li
              className={`flex items-center space-x-2.5 ${
                step === 1
                  ? 'text-blue-600 dark:text-blue-500 animate-pulse'
                  : step > 1
                    ? 'text-green-500 dark:text-green-400'
                    : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {step > 1 ? (
                <span className="flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -start-4 ring-4 ring-white dark:ring-gray-900 dark:bg-green-900">
                  <LuCheck className="text-green-500 dark:text-green-400" size={20} />
                </span>
              ) : (
                <span
                  className={`flex items-center justify-center w-8 h-8 border rounded-full shrink-0 ${
                    step === 1 ? 'border-blue-600 dark:border-blue-500' : 'border-gray-500 dark:border-gray-400'
                  }`}
                >
                  1
                </span>
              )}
              <span className={`${step === 1 ? 'flex flex-col' : 'hidden md:flex md:flex-col'}`}>
                <h3 className="font-medium leading-tight">Archivo</h3>
                <p className="text-sm">Subir archivo</p>
              </span>
            </li>

            {/* 2 */}
            <li
              className={`flex items-center space-x-2.5 ${
                step === 2
                  ? rowLogs?.filter((x) => x?.type === 'error')?.length > 0
                    ? 'text-red-500 dark:text-red-400 animate-pulse'
                    : 'text-blue-600 dark:text-blue-500 animate-pulse'
                  : step > 2
                    ? 'text-green-500 dark:text-green-400'
                    : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {step > 2 ? (
                <span className="flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -start-4 ring-4 ring-white dark:ring-gray-900 dark:bg-green-900">
                  <LuCheck className="text-green-500 dark:text-green-400" size={20} />
                </span>
              ) : rowLogs?.filter((x) => x?.type === 'error')?.length > 0 ? (
                <span className="flex items-center justify-center w-8 h-8 bg-red-200 rounded-full -start-4 ring-4 ring-white dark:ring-gray-900 dark:bg-red-900">
                  <LuX className="text-red-500 dark:text-red-400" size={20} />
                </span>
              ) : (
                <span
                  className={`flex items-center justify-center w-8 h-8 border rounded-full shrink-0 ${
                    step === 2 ? 'border-blue-600 dark:border-blue-500' : 'border-gray-500 dark:border-gray-400'
                  }`}
                >
                  2
                </span>
              )}
              <span className={`${step === 2 ? 'flex flex-col' : 'hidden md:flex md:flex-col'}`}>
                <h3 className="font-medium leading-tight">Analizar</h3>
                <p className="text-sm">Analizar archivo</p>
              </span>
            </li>

            {/* 3 */}
            <li
              className={`flex items-center space-x-2.5 ${
                step === 3
                  ? 'text-blue-600 dark:text-blue-500 animate-pulse'
                  : step > 3
                    ? importError
                      ? 'text-red-500 dark:text-red-400'
                      : 'text-green-500 dark:text-green-400'
                    : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {step > 3 ? (
                importError ? (
                  <span className="flex items-center justify-center w-8 h-8 bg-red-200 rounded-full -start-4 ring-4 ring-white dark:ring-gray-900 dark:bg-red-900">
                    <LuX className="text-red-500 dark:text-red-400" size={20} />
                  </span>
                ) : (
                  <span className="flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -start-4 ring-4 ring-white dark:ring-gray-900 dark:bg-green-900">
                    <LuCheck className="text-green-500 dark:text-green-400" size={20} />
                  </span>
                )
              ) : (
                <span
                  className={`flex items-center justify-center w-8 h-8 border rounded-full shrink-0 ${
                    step === 3 ? 'border-blue-600 dark:border-blue-500' : 'border-gray-500 dark:border-gray-400'
                  }`}
                >
                  3
                </span>
              )}
              <span className={`${step === 3 ? 'flex flex-col' : 'hidden md:flex md:flex-col'}`}>
                <h3 className="font-medium leading-tight">Importar</h3>
                <p className="text-sm">Importar registros</p>
              </span>
            </li>

            {/* 4 */}
            <li
              className={`flex items-center space-x-2.5 ${
                step === 4
                  ? importError
                    ? 'text-red-500 dark:text-red-400 animate-pulse'
                    : 'text-green-500 dark:text-green-400 animate-pulse'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {step === 4 ? (
                importError ? (
                  <span className="flex items-center justify-center w-8 h-8 bg-red-200 rounded-full -start-4 ring-4 ring-white dark:ring-gray-900 dark:bg-red-900">
                    <LuX className="text-red-500 dark:text-red-400" size={20} />
                  </span>
                ) : (
                  <span className="flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -start-4 ring-4 ring-white dark:ring-gray-900 dark:bg-green-900">
                    <LuCheck className="text-green-500 dark:text-green-400" size={20} />
                  </span>
                )
              ) : (
                <span className="flex items-center justify-center w-8 h-8 border border-gray-500 rounded-full shrink-0 dark:border-gray-400">
                  4
                </span>
              )}
              <span className={`${step === 4 ? 'flex flex-col' : 'hidden md:flex md:flex-col'}`}>
                <h3 className="font-medium leading-tight">Resumen</h3>
                <p className="text-sm">Resumen importación</p>
              </span>
            </li>
          </ol>

          {/* Progress */}
          <div className="w-full my-5 bg-gray-200 rounded-full dark:bg-gray-700">
            <div
              className={`${
                rowLogs?.filter((x) => x?.type === 'error')?.length > 0 && step === 2 ? 'bg-red-500' : 'bg-blue-500'
              } text-xs font-medium text-white text-center p-0.5 leading-none rounded-full`}
              style={{
                width: step === 1 ? '25%' : step === 2 ? '50%' : step === 3 ? '75%' : step === 4 ? '100%' : '0%'
              }}
            >
              {step === 1 ? '25%' : step === 2 ? '50%' : step === 3 ? '75%' : step === 4 ? '100%' : null}
            </div>
          </div>

          {/* Body */}
          {(() => {
            switch (step) {
              case 1:
                return (
                  <div
                    id="drop_zone"
                    className="flex items-center justify-center w-full grow"
                    onDrop={dropHandler}
                    onDragOver={(e) => {
                      setDragOver(true)
                      dragOverHandler(e)
                    }}
                    onDragLeave={() => setDragOver(false)}
                  >
                    <label
                      htmlFor="dropzone-file"
                      className={`flex flex-col items-center justify-center w-full h-full border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600 ${
                        dragOver ? 'bg-gray-100 dark:bg-gray-600' : 'bg-gray-50 dark:bg-gray-700'
                      }`}
                    >
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <LuCloudUpload className="text-gray-500 dark:text-gray-400" size={30} />
                        <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                          <span className="font-semibold">Haz clic para subir</span> o arrastra y suelta
                        </p>
                        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Solo archivos Excel (.xlsx)</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          <span className="font-semibold">
                            {file ? `Archivo subido: ${file?.name}` : 'No se ha subido ningún archivo aún'}
                          </span>
                        </p>
                      </div>

                      <input
                        id="dropzone-file"
                        type="file"
                        className="hidden"
                        accept=".xlsx"
                        onChange={uploadDocument}
                      />
                    </label>
                  </div>
                )

              case 2:
                return (
                  <div className="flex flex-col items-center justify-center w-full h-48 py-5 grow">
                    {loading ? (
                      <p className="flex flex-col-reverse items-center justify-center h-full font-bold dark:text-[#F5F5F5] text-gray-700">
                        <LuLoaderCircle size={32} className="animate-spin text-[#5361AD]" />
                      </p>
                    ) : (
                      <>
                        <div className="flex items-center justify-center">
                          <LuCloudAlert className="text-yellow-500 dark:text-yellow-400" size={50} />
                        </div>

                        <div className="w-full text-left">
                          <p className="my-2 text-gray-500 dark:text-gray-400">
                            ¿Seguro que quieres importar <span className="font-semibold">{analyzedData?.length}</span>{' '}
                            registros? (Esto reemplaza el listado completo)
                          </p>
                          <p className="my-1 text-xs text-gray-400">
                            Tras la importación, se sincronizarán automáticamente los siniestros por delegación.
                          </p>
                        </div>

                        <div className="flex flex-col w-full p-2 overflow-y-auto text-left border-2 border-gray-300 rounded-lg grow bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                          {rowLogs?.length ? (
                            <div className="w-full space-y-2">
                              {rowLogs.map((rowLog, idx) => (
                                <div key={idx} className="flex flex-row items-start text-gray-500 dark:text-gray-400">
                                  <div className="pt-px">
                                    {rowLog.type === 'import' ? (
                                      <LuCheck className="pr-1 text-green-500 w-fit min-w-fit dark:text-green-400" size={22} />
                                    ) : rowLog.type === 'warning' ? (
                                      <LuTriangleAlert
                                        className="pr-1 text-yellow-500 w-fit min-w-fit dark:text-yellow-400"
                                        size={22}
                                      />
                                    ) : rowLog.type === 'error' ? (
                                      <LuX className="pr-1 text-red-500 w-fit min-w-fit dark:text-red-400" size={22} />
                                    ) : (
                                      <LuCloudAlert className="pr-1 w-fit min-w-fit" size={22} />
                                    )}
                                  </div>
                                  <span className="grow">{rowLog.message}</span>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>

                        <div className="flex flex-row justify-between w-full text-left">
                          <div className="flex flex-row items-center my-2 text-gray-500 dark:text-gray-400">
                            <LuCheck className="pr-1 text-green-500 dark:text-green-400 w-fit min-w-fit" size={22} />
                            {rowLogs?.filter((x) => x?.type === 'import')?.length || 0}
                            <span className="hidden pl-1 md:flex">correctos</span>
                          </div>

                          <div className="flex flex-row items-center my-2 text-gray-500 dark:text-gray-400">
                            <LuTriangleAlert className="pr-1 text-yellow-500 dark:text-yellow-400 w-fit min-w-fit" size={22} />
                            {rowLogs?.filter((x) => x?.type === 'warning')?.length || 0}
                            <span className="hidden pl-1 md:flex">advertencias</span>
                          </div>

                          <div className="flex flex-row items-center my-2 text-gray-500 dark:text-gray-400">
                            <LuX className="pr-1 text-red-500 dark:text-red-400 w-fit min-w-fit" size={22} />
                            {rowLogs?.filter((x) => x?.type === 'error')?.length || 0}
                            <span className="hidden pl-1 md:flex">errores</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )

              case 3:
                return (
                  <div className="flex flex-col items-center justify-center w-full h-48 grow">
                    <div className="flex items-center justify-center">
                      <LuLoaderCircle size={32} className="animate-spin text-[#5361AD]" />
                    </div>

                    <div className="text-center">
                      <p className="text-gray-500 dark:text-gray-400">
                        Importando registros. Esto tomará unos momentos.
                      </p>
                    </div>
                  </div>
                )

              case 4:
                return (
                  <div className="flex flex-col items-start justify-start w-full h-48 py-5 space-y-2 grow">
                    {importError ? (
                      <div className="flex flex-col justify-between w-full space-y-2 text-left">
                        <div className="flex flex-row items-center text-gray-500 dark:text-gray-400">
                          <LuX className="pr-1 text-red-500 dark:text-red-400" size={22} />
                          <span>{importError}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col justify-between w-full space-y-2 text-left">
                        <div className="flex flex-row items-start text-gray-500 dark:text-gray-400">
                          <div className="pt-px">
                            <LuCheck className="pr-1 text-green-500 dark:text-green-400 w-fit min-w-fit" size={22} />
                          </div>
                          <span className="grow">
                            Importación completada. Se han procesado {processedRowsCount} registros.
                          </span>
                        </div>

                        {duplicatedRowsCount > 0 ? (
                          <div className="flex flex-row items-start text-gray-500 dark:text-gray-400">
                            <div className="pt-px">
                              <LuRefreshCcw className="pr-1 text-green-500 dark:text-green-400 w-fit min-w-fit" size={22} />
                            </div>
                            <span className="grow">{duplicatedRowsCount} registros existían y se actualizaron.</span>
                          </div>
                        ) : null}

                        {insertedRowsCount > 0 ? (
                          <div className="flex flex-row items-start text-gray-500 dark:text-gray-400">
                            <div className="pt-px">
                              <LuArrowDownToLine className="pr-1 text-green-500 dark:text-green-400 w-fit min-w-fit" size={22} />
                            </div>
                            <span className="grow">{insertedRowsCount} registros importados.</span>
                          </div>
                        ) : null}

                        {/* NUEVO: resumen sincronización siniestros */}
                        <div className="flex flex-row items-start text-gray-500 dark:text-gray-400">
                          <div className="pt-px">
                            <LuCheck className="pr-1 text-green-500 dark:text-green-400 w-fit min-w-fit" size={22} />
                          </div>
                          <span className="grow">
                            Siniestros sincronizados por delegación: {syncedSiniestrosCount}.
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )

              default:
                return null
            }
          })()}

          {/* Footer buttons */}
          <div className="flex flex-row items-center justify-between mt-5 space-x-2">
            <div className="flex items-center justify-between gap-x-4">
              <button
                type="button"
                onClick={() => onClose()}
                className={`${step > 2 && 'invisible'} flex items-center justify-center w-fit px-3 py-2 space-x-2 text-sm text-white transition-colors duration-200 bg-gray-500 border border-gray-500 rounded-sm md:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-gray-500 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700`}
              >
                <LuX size={20} />
                <span className="hidden md:flex">Cerrar</span>
              </button>
            </div>

            <div className="flex flex-row space-x-2 sm:items-center sm:justify-between sm:gap-x-4">
              {step === 2 && (
                <button
                  type="button"
                  onClick={() => {
                    setStep(1)
                    setRowLogs([])
                    setAnalyzedData(false)
                    setFile(null)
                    setImportError(null)
                    setSyncedSiniestrosCount(0)
                    setProcessedRowsCount(0)
                    setInsertedRowsCount(0)
                    setDuplicatedRowsCount(0)
                  }}
                  className={`${step > 2 && 'invisible'} flex items-center justify-center w-fit px-3 py-2 space-x-2 text-sm text-white transition-colors duration-200 bg-gray-500 border border-gray-500 rounded-sm md:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-gray-500 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700`}
                >
                  <LuMoveLeft size={20} />
                  <span className="hidden md:flex">Volver</span>
                </button>
              )}

              <button
                type="button"
                onClick={async () => {
                  if (step === 1) {
                    setLoading(1)
                    setStep(2)

                    try {
                      const result = await handleAnalyzeExcel(file)
                      setRowLogs(result.logs || [])
                      setAnalyzedData(result.data || [])
                    } catch (e) {
                      setRowLogs([{ type: 'error', message: e?.message || String(e) }])
                      setAnalyzedData([])
                    } finally {
                      setLoading(0)
                    }
                  }

                  if (step === 2) {
                    setLoading(1)
                    setStep(3)
                    await handleUploadExcel(uploadExcelURL, analyzedData, save)
                    setLoading(0)
                  }

                  if (step === 4) {
                    handleRefresh()
                    onClose()
                  }
                }}
                className={`${step === 3 && 'invisible'} flex items-center justify-center w-fit px-3 py-2 space-x-2 md:w-36 text-sm text-white transition-colors duration-200 bg-orange-500 border border-orange-500 rounded-sm hover:bg-orange-600 hover:border-orange-600 disabled:opacity-30`}
                disabled={!file || (step === 2 && rowLogs?.filter((x) => x?.type === 'error')?.length > 0)}
              >
                {step === 1 ? (
                  <>
                    <LuSearch size={20} />
                    <span className="hidden md:flex">Analizar</span>
                  </>
                ) : step === 2 ? (
                  <>
                    <LuUpload size={20} />
                    <span className="hidden md:flex">Importar</span>
                  </>
                ) : step === 4 ? (
                  <>
                    <LuCheck size={20} />
                    <span className="hidden md:flex">Finalizar</span>
                  </>
                ) : (
                  <>
                    <span className="hidden md:flex">Continuar</span>
                    <LuMoveRight size={20} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

UploadFileModal.propTypes = {
  onClose: PropTypes.func,
  uploadExcelURL: PropTypes.string,
  handleRefresh: PropTypes.func,
  handleAnalyzeExcel: PropTypes.func
}
